import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Gem, Loader2 } from 'lucide-react';
import { useGetMyClub } from '@/services/queries/club';
import { processClubBenefitsForUser } from '@/utils/clubMonthlySummary';
import type { ClubProcessedBenefit } from '@/utils/clubMonthlySummary';
import {
	ClubMonthlySummaryCard,
	ClubMonthlySummaryHeader,
} from '@/pages/user/clubs/ClubMonthlySummaryCard';
import { ClubConsumableBenefitsSection } from '@/pages/user/clubs/ClubConsumableBenefitsSection';
import { buildClubBenefitConsultaPath } from '@/pages/user/clubs/clubBenefitActions';
import { BenefitRedeemModal } from '@/pages/user/clubs/BenefitRedeemModal';
import { useModal } from '@/hooks';
import { userHomeDescriptionAccentLinkClassName } from './userHomeCardStyles';

const UserHomeClubSection = () => {
	const navigate = useNavigate();
	const { openModal } = useModal();
	const { data: myClub, isLoading } = useGetMyClub();

	const hasActiveSubscription = myClub?.subscription?.status === 'ACTIVE';

	const { clubName, monthlySummary, consumableBenefits } = useMemo(() => {
		if (!hasActiveSubscription || !myClub?.subscription) {
			return {
				clubName: '',
				monthlySummary: null,
				consumableBenefits: [] as ClubProcessedBenefit[],
			};
		}

		const currentClub = myClub.subscription.club;
		const { monthlySummary: summary, processedBenefits } =
			processClubBenefitsForUser(myClub.subscription, currentClub);

		const consumable = processedBenefits.filter(
			(benefit) =>
				benefit.renewalTrigger === 'ALWAYS_ON_SUBSCRIPTION_PAYMENT' ||
				benefit.renewalTrigger === 'ALWAYS_ON_USE',
		);

		return {
			clubName: currentClub?.name || 'Seu clube',
			monthlySummary: summary,
			consumableBenefits: consumable,
		};
	}, [hasActiveSubscription, myClub]);

	const handleUseBenefit = (benefit: ClubProcessedBenefit) => {
		navigate(buildClubBenefitConsultaPath(benefit));
	};

	const handleRedeemBenefit = (benefit: ClubProcessedBenefit) => {
		openModal({
			title: null,
			content: (
				<BenefitRedeemModal
					title="Resgate de benefício"
					description={benefit.description || null}
					origin={
						(benefit.serviceOrigin ?? 'INTERNAL') as Services.Origin
					}
				/>
			),
		});
	};

	if (isLoading) {
		return (
			<div className="flex justify-center py-12">
				<Loader2 className="animate-spin text-brand-orange" size={32} />
			</div>
		);
	}

	if (!hasActiveSubscription || !monthlySummary) {
		return null;
	}

	return (
		<section className="w-full space-y-8 pb-16 md:pb-24">
			<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
				<div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-brand-orange/10 text-brand-orange">
					<Gem size={24} />
				</div>
				<div className="space-y-1">
					<p className="text-xs font-semibold uppercase tracking-wider text-brand-orange">
						Meu clube
					</p>
					<h2 className="text-2xl font-bold tracking-tight text-zinc-900 md:text-3xl">
						{clubName}
					</h2>
					<p className="text-sm text-zinc-500">
						Acompanhe o uso dos seus benefícios neste mês.{' '}
						<button
							type="button"
							onClick={() => navigate('/clubes')}
							className={userHomeDescriptionAccentLinkClassName}
						>
							Ver mais informações
							<ChevronRight
								size={14}
								className="transition-transform group-hover:translate-x-0.5"
								aria-hidden
							/>
						</button>
					</p>
				</div>
			</div>

			<div className="w-full space-y-6">
				<div className="space-y-6">
					<ClubMonthlySummaryHeader titleClassName="text-2xl" />
					<ClubMonthlySummaryCard
						hideHeader
						summary={monthlySummary}
					/>
				</div>
				<ClubConsumableBenefitsSection
					benefits={consumableBenefits}
					onUseBenefit={handleUseBenefit}
					onRedeemBenefit={handleRedeemBenefit}
					columns={3}
				/>
			</div>
		</section>
	);
};

export { UserHomeClubSection };
